import type {
  NavigationFailure,
  NavigationGuardWithThis,
  NavigationHookAfter,
  RouteLocation,
  RouteLocationNormalizedLoaded,
  RouteLocationRaw,
  Router,
} from 'vue-router';

import useRouteManagerStore from '~/stores/routeManager';
import { isNonProd, isSSRHydration } from '~/utils/EnvUtils';
import logger from './QLogger';

export const setRouteSourceName = (source: string) => useRouteManagerStore().setSourceName(source);

export interface IQRouter {
  push(source: string, to: RouteLocationRaw): Promise<NavigationFailure | void | undefined>,
  forward(): void,
  back(): void,
  go(delta: number): void,
  resolve(to: RouteLocationRaw, currentLocation?: RouteLocationNormalizedLoaded): RouteLocation & {
    href: string,
  },
  replace(source: string, to: RouteLocationRaw): Promise<NavigationFailure | void | undefined>,
  beforeEach(guard: NavigationGuardWithThis<undefined>): () => void,
  afterEach(guard: NavigationHookAfter): () => void,
}

export default class QRouter implements IQRouter {
  public static instance: QRouter | null = null;

  router: Router;

  createError: (msg: string, shouldThrow?: boolean) => void;

  private constructor(router: Router) {
    this.router = router;
    this.createError = (msg: string, shouldThrow = false) => {
      const err = new Error(`\nQRouter Exception :: ${msg}\n`);
      if (shouldThrow) {
        throw err;
      } else {
        logger.logOnSentry(['type: QRouter.exception', `message: ${msg}`], 'exception');
      }
    };
  }

  push(source: string, to: RouteLocationRaw): Promise<void | NavigationFailure | undefined> {
    setRouteSourceName(source ?? 'push');
    if (!source) {
      this.createError('source not provided in QRouter.push()', isNonProd());
    }
    if (!to) {
      this.createError('no \'to\' provided in QRouter.push()', true);
    }

    if (isSSRHydration() && (to instanceof String || (to instanceof Object && 'path' in to))) {
      const newRoute = to instanceof String ? to : to.path;
      if (newRoute !== window.location.pathname) {
        window.location.href = `${window.location.origin}${newRoute}` as string & Location;
      }
    }

    return this.router.push(to);
  }

  forward(): void {
    setRouteSourceName('forward');
    this.router.forward();
  }

  back(): void {
    setRouteSourceName('back');
    this.router.back();
  }

  go(delta: number): void {
    setRouteSourceName('go');
    this.router.go(delta);
  }

  resolve(to: RouteLocationRaw, currentLocation?: RouteLocationNormalizedLoaded | undefined): RouteLocation & { href: string } {
    return this.router.resolve(to, currentLocation);
  }

  replace(source: string, to: RouteLocationRaw): Promise<void | NavigationFailure | undefined> {
    if (!source) {
      this.createError('source not provided in QRouter.replace()', isNonProd());
    }
    setRouteSourceName(source ?? 'replace');
    if (!to) {
      this.createError('no \'to\' provided in QRouter.replace()', true);
    }
    return this.router.replace(to);
  }

  beforeEach(guard: NavigationGuardWithThis<undefined>): () => void {
    return this.router.beforeEach(guard);
  }

  afterEach(guard: NavigationHookAfter) {
    return this.router.afterEach(guard);
  }

  static getInstance(router: Router) {
    if (!QRouter.instance) {
      QRouter.instance = new QRouter(router);
    }

    return QRouter.instance;
  }
}
